import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Globe,
  KeyRound,
  Loader2,
  Plus,
  ShieldCheck,
  Ticket,
  Trash2,
  Unlock,
  UserCheck
} from "lucide-react";
import {
  checkConnection,
  defineApi,
  saveConnection,
  setLogoFromSite,
  uploadLogo
} from "../../services/inHouseService";
import {
  Card,
  CodeBlock,
  ErrorNote,
  Field,
  IntegrationLogo,
  SecretInput,
  SettingRow,
  StepFooter,
  checkBaseUrl,
  hostOf,
  inputClass
} from "./parts";

/** Steps 1 (Basics) and 2 (Connection). */

const AUTH_TYPES = [
  {
    id: "none",
    icon: Unlock,
    label: "No sign-in",
    plain: "Anyone with the address can call it. Rare for internal systems.",
    tech: "No auth header"
  },
  {
    id: "api_key",
    icon: KeyRound,
    label: "API key",
    plain: "A secret string your IT team gives you. Most common.",
    tech: "Key sent in a header or query parameter"
  },
  {
    id: "bearer",
    icon: Ticket,
    label: "Access token",
    plain: "A long-lived token, often shown as “Bearer …” in docs.",
    tech: "Authorization: Bearer <token>"
  },
  {
    id: "oauth_client_credentials",
    icon: UserCheck,
    label: "Client ID & secret",
    plain: "Orbit signs in on your behalf, e.g. with Microsoft Entra or Okta.",
    tech: "OAuth 2.0 client credentials grant"
  }
];

/** The plain-language name of a sign-in method, as the Connection step shows it. */
export const authLabel = (kind) => AUTH_TYPES.find((t) => t.id === kind)?.label || "Not set";

// Which connection auth_type stores the secret for each sign-in method.
const STORED_AS = {
  none: "no_auth",
  api_key: "api_key",
  bearer: "api_key",
  oauth_client_credentials: "oauth"
};

const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"]; // what the logo upload accepts
const MAX_LOGO_BYTES = 1024 * 1024;

/**
 * The logo, with two ways to set it: the API site's own icon (found by the server, so the
 * customer's hostname never goes to a third-party favicon service) or an upload.
 */
function LogoPicker({ integration, name, logo }) {
  const [state, setState] = useState("idle"); // idle | finding | uploading
  const [message, setMessage] = useState("");
  if (!integration) return <IntegrationLogo name={name} className="h-14 w-14 text-xl" />;

  const apply = async (state, request) => {
    setState(state);
    setMessage("");
    const result = await request();
    setState("idle");
    if (result.success) logo?.reload();
    else setMessage(logo?.url ? `${result.message} Your current logo is unchanged.` : result.message);
  };

  const pickFile = (input) => {
    const file = input.files?.[0];
    input.value = ""; // so picking the same file again still fires
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) setMessage("Use a PNG, JPEG or WebP image.");
    else if (file.size > MAX_LOGO_BYTES) setMessage("The image is larger than 1 MB.");
    else apply("uploading", () => uploadLogo(integration.id, file));
  };

  const busy = state !== "idle";
  return (
    <div className="flex w-28 shrink-0 flex-col items-center gap-1.5 text-center">
      <div className="relative">
        <IntegrationLogo name={name} logoUrl={logo?.url} className="h-14 w-14 text-xl ring-1 ring-gray-200" />
        {busy && (
          <span className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/70">
            <Loader2 className="h-5 w-5 animate-spin text-gray-500" aria-label={state === "finding" ? "Finding the site's icon" : "Uploading"} />
          </span>
        )}
      </div>
      <label className={`text-xs font-medium text-blue-600 hover:text-blue-700 ${busy ? "pointer-events-none opacity-50" : "cursor-pointer"}`}>
        {logo?.url ? "Replace logo" : "Upload logo"}
        <input type="file" accept={LOGO_TYPES.join(",")} className="sr-only" disabled={busy} onChange={(e) => pickFile(e.target)} />
      </label>
      {integration.api?.base_url && (
        <button
          type="button"
          disabled={busy}
          onClick={() => apply("finding", () => setLogoFromSite(integration.id))}
          title={`Use the icon of ${hostOf(integration.api.base_url)}`}
          className="text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
        >
          Use the site's icon
        </button>
      )}
      {message && (
        <p role="alert" className="text-xs text-rose-600 [overflow-wrap:anywhere]">
          {message}
        </p>
      )}
    </div>
  );
}

export function BasicsStep({ integration, logo, onSave, onNext, submitLabel = "Continue" }) {
  const [name, setName] = useState(integration?.name || "");
  const [description, setDescription] = useState(integration?.description || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setBusy(true);
    setError("");
    const result = await onSave({ name: name.trim(), description: description || null });
    setBusy(false);
    if (result.success) onNext();
    else setError(result.message);
  };

  return (
    <>
      <Card
        title="What is this API?"
        subtitle="This is how your team will recognize it across Orbit."
      >
        <div className="flex gap-5">
          <LogoPicker integration={integration} name={name} logo={logo} />
          <div className="flex-1 space-y-5">
            <Field label="Name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                placeholder="e.g. Policy Admin API"
                className={inputClass(false)}
              />
            </Field>
            <Field
              label="Description"
              optional
              hint="One sentence on what it does. The AI builder reads this too."
            >
              <textarea
                rows={2}
                maxLength={1000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Create and look up quotes in our policy system."
                className={inputClass(false)}
              />
            </Field>
          </div>
        </div>
        <ErrorNote message={error} />
      </Card>
      <StepFooter
        nextLabel={submitLabel}
        busy={busy}
        disabled={!name.trim()}
        onNext={save}
      />
    </>
  );
}

const DEFAULT_AUTH = { kind: "api_key", location: "header", name: "X-API-Key" };

const authBody = (auth) => {
  if (auth.kind === "api_key")
    return { kind: "api_key", location: auth.location || "header", name: auth.name || "X-API-Key" };
  if (auth.kind === "oauth_client_credentials")
    return { kind: auth.kind, token_url: auth.token_url, scopes: auth.scopes || null };
  return { kind: auth.kind };
};

const sameEntries = (a = {}, b = {}) =>
  JSON.stringify(Object.entries(a).sort()) === JSON.stringify(Object.entries(b).sort());

// The server stores the base URL without a trailing slash.
const normalizeUrl = (url) => (url || "").trim().replace(/\/+$/, "");

/** True when the address and sign-in method are what is already saved. */
const isApiUnchanged = (api, baseUrl, auth) =>
  !!api &&
  normalizeUrl(api.base_url) === normalizeUrl(baseUrl) &&
  JSON.stringify(authBody(api.auth)) === JSON.stringify(authBody(auth));

// The credential fields each sign-in method sends; anything typed for another method is dropped.
const CREDENTIAL_KEYS = {
  none: [],
  api_key: ["api_key"],
  bearer: ["token"],
  oauth_client_credentials: ["client_id", "client_secret"]
};

const credentialsFor = (kind, creds) =>
  Object.fromEntries((CREDENTIAL_KEYS[kind] || []).filter((k) => creds[k]).map((k) => [k, creds[k]]));

/**
 * A saved secret is reused only for the same method, sent the same way: a key typed for
 * one header, query parameter or token URL must not be sent anywhere else.
 */
const sameSecretTarget = (saved, auth) => {
  if (!saved || saved.kind !== auth.kind) return false;
  if (auth.kind === "api_key")
    return (saved.location || "header") === (auth.location || "header") &&
      (saved.name || "X-API-Key") === (auth.name || "X-API-Key");
  if (auth.kind === "oauth_client_credentials") return saved.token_url === auth.token_url;
  return true;
};

/** True when the saved connection is on, has what it needs, and nothing new was entered. */
const isConnectionUnchanged = (connection, body, authKind) =>
  !!connection?.connected &&
  (connection.secret_configured || authKind === "none") &&
  !Object.values(body.credentials).some(Boolean) &&
  sameEntries(connection.custom_headers, body.custom_headers) &&
  connection.request_timeout_ms === body.request_timeout_ms &&
  connection.max_retries === body.max_retries;

const credentialsReady = (auth, creds, keepSaved) => {
  if (auth.kind === "none" || keepSaved) return true;
  if (auth.kind === "api_key") return !!creds.api_key;
  if (auth.kind === "bearer") return !!creds.token;
  return !!creds.client_id && !!creds.client_secret;
};

/**
 * `proposed` is what an imported file suggested when it couldn't be saved as the
 * API yet (e.g. the server refused the address), so nothing typed is lost.
 */
export function ConnectionStep({ integration, refresh, devMode, logo, proposed, onBack, onNext }) {
  const connection = integration.connection;
  const [baseUrl, setBaseUrl] = useState(integration.api?.base_url || proposed?.base_url || "");
  const [auth, setAuth] = useState(integration.api?.auth || proposed?.auth || DEFAULT_AUTH);
  const [creds, setCreds] = useState({});
  const [headers, setHeaders] = useState(
    Object.entries(connection?.custom_headers || {}).map(([key, value]) => ({ key, value }))
  );
  const [timeoutMs, setTimeoutMs] = useState(connection?.request_timeout_ms || 30000);
  const [retries, setRetries] = useState(connection?.max_retries ?? 3);
  const [state, setState] = useState("idle"); // idle | saving | checking
  const [check, setCheck] = useState(null);
  const [error, setError] = useState("");

  const urlCheck = checkBaseUrl(baseUrl);
  // A saved secret can be kept only while the sign-in method still stores it the same way.
  const keepSaved =
    !!connection?.secret_configured &&
    connection.auth_type === STORED_AS[auth.kind] &&
    sameSecretTarget(integration.api?.auth, auth);
  const tokenUrlCheck = auth.kind === "oauth_client_credentials" ? checkBaseUrl(auth.token_url) : {};
  const keyNameError =
    auth.kind === "api_key" && !/^[!#$%&'*+\-.^_`|~0-9A-Za-z]{1,128}$/.test(auth.name || "")
      ? "Use letters, numbers and dashes only, e.g. X-API-Key"
      : null;
  const ready =
    urlCheck.ok &&
    !keyNameError &&
    credentialsReady(auth, creds, keepSaved) &&
    (auth.kind !== "oauth_client_credentials" || !!tokenUrlCheck.ok);
  // Saved, but switched off: by Disable, or because the sign-in method changed.
  const paused = !!connection && !connection.connected && integration.status !== "draft";

  const setAuthField = (patch) => setAuth((a) => ({ ...a, ...patch }));

  const connectionBody = {
    credentials: credentialsFor(auth.kind, creds),
    custom_headers: Object.fromEntries(headers.filter((h) => h.key).map((h) => [h.key, h.value])),
    request_timeout_ms: Number(timeoutMs),
    max_retries: Number(retries)
  };
  const apiUnchanged = isApiUnchanged(integration.api, baseUrl, auth);
  const unsaved = !(apiUnchanged && isConnectionUnchanged(connection, connectionBody, auth.kind));
  const savedUrl = integration.api?.base_url;
  const urlMoved = !!savedUrl && normalizeUrl(baseUrl) !== normalizeUrl(savedUrl);

  // Save and Check are separate: saving only stores the details, checking only tests what is stored.
  const save = async () => {
    setState("saving");
    setError("");
    // Each save is several round trips to the database; skip the parts that didn't change.
    const steps = [
      !apiUnchanged &&
        (() => defineApi(integration.id, { base_url: normalizeUrl(baseUrl), auth: authBody(auth) })),
      () => saveConnection(integration.id, connectionBody)
    ].filter(Boolean);
    let last;
    for (const step of steps) {
      last = await step();
      if (!last.success) break;
    }
    await refresh();
    setState("idle");
    if (!last.success) {
      setError(last.message);
      return;
    }
    setCreds({});
    // A new address with no logo yet: try the site's own icon in the background. A logo the
    // admin chose is never replaced, and a miss is fine (the first letter stays).
    if (!logo?.url && (urlMoved || !savedUrl)) {
      setLogoFromSite(integration.id).then((r) => r.success && logo?.reload());
    }
  };

  // Checks the form as it is, saved or not; a result goes stale as soon as the form changes.
  const draft = { api: { base_url: normalizeUrl(baseUrl), auth: authBody(auth) }, credentials: connectionBody.credentials };
  const draftKey = JSON.stringify(draft);
  const currentCheck = check?.key === draftKey ? check.result : null;
  const checkable = !!urlCheck.ok && !keyNameError && (auth.kind !== "oauth_client_credentials" || !!tokenUrlCheck.ok);

  const runCheck = async () => {
    setState("checking");
    setError("");
    const result = await checkConnection(integration.id, draft);
    setState("idle");
    if (result.success) setCheck({ key: draftKey, result: result.data });
    else setError(result.message);
  };

  const selected = AUTH_TYPES.find((t) => t.id === auth.kind);

  return (
    <>
      <div className="space-y-4">
        {proposed?.note && (
          <Notice tone="amber" title="Check the address from your file">
            Orbit couldn't save it yet: {proposed.note}
          </Notice>
        )}
        {paused && (
          <Notice tone="amber" title="Calls are paused">
            {integration.status === "disabled" && connection.secret_configured
              ? "This integration is disabled. Turn it back on from Go live."
              : "The sign-in method changed, so the old details were removed. Enter the new ones and save to resume."}
          </Notice>
        )}
        {/* One settings card, labels beside their controls (as Stripe and Vercel lay out settings), so the step fits on one screen. */}
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="divide-y divide-gray-100">
            <SettingRow title="Base URL" hint="The address every call starts with.">
              <Field
                error={urlCheck.error}
                success={urlCheck.ok && (urlCheck.warning || "Looks good. Orbit checks this address again on every call.")}
                hint="Must be https:// and reachable from the internet."
              >
                <UrlInput value={baseUrl} onChange={setBaseUrl} invalid={!!urlCheck.error} />
              </Field>
              {urlMoved && (
                <div role="status" className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">
                      {integration.status === "draft"
                        ? "Every call will go to this new address once you save."
                        : "This integration is in use. Once you save, every call, including those in workflows, goes to the new address."}
                    </p>
                    <p className="mt-0.5 truncate font-mono text-amber-800" title={savedUrl}>Was {savedUrl}</p>
                  </div>
                  <button type="button" onClick={() => setBaseUrl(savedUrl)} className="shrink-0 font-medium text-amber-900 underline hover:no-underline">
                    Undo
                  </button>
                </div>
              )}
            </SettingRow>

            <SettingRow title="Sign-in" hint="Not sure? Your IT team or the API docs will say. API key is the most common.">
              <div role="radiogroup" aria-label="How Orbit signs in" className="grid grid-cols-2 gap-2">
                {AUTH_TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = auth.kind === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setAuthField({ kind: t.id })}
                      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                        active
                          ? "border-blue-600 bg-blue-50/50 text-gray-900 ring-1 ring-blue-600"
                          : "border-gray-200 text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${active ? "text-blue-600" : "text-gray-400"}`} />
                      {t.label}
                      {active && <CheckCircle2 className="ml-auto h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-gray-500">{devMode ? selected?.tech : selected?.plain}</p>

              {auth.kind !== "none" && (
                <div className="mt-5 space-y-4">
                  <CredentialFields
                    auth={auth}
                    creds={creds}
                    keepSaved={keepSaved}
                    setAuthField={setAuthField}
                    setCreds={(patch) => setCreds((c) => ({ ...c, ...patch }))}
                    keyNameError={keyNameError}
                    tokenUrlError={tokenUrlCheck.error}
                  />
                  <p className="flex items-center gap-2 text-xs text-gray-500">
                    <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" />
                    Encrypted as soon as you save. Nobody, including you, can view it again; you can only replace it.
                  </p>
                </div>
              )}
            </SettingRow>

            {devMode && (
              <AdvancedSettings
                auth={auth}
                baseUrl={baseUrl}
                headers={headers}
                setHeaders={setHeaders}
                timeoutMs={timeoutMs}
                setTimeoutMs={setTimeoutMs}
                retries={retries}
                setRetries={setRetries}
              />
            )}
          </div>

          <ConnectionStatus
            connected={!!connection?.connected}
            check={currentCheck}
            devMode={devMode}
            ready={ready}
            checkable={checkable}
            unsaved={unsaved}
            state={state}
            onSave={save}
            onCheck={runCheck}
          />
        </section>
        <ErrorNote message={error} />
      </div>
      <StepFooter
        onBack={onBack}
        onNext={onNext}
        disabled={!connection?.connected || unsaved}
        hint={connection?.connected && unsaved ? "Save your changes first" : undefined}
      />
    </>
  );
}

const MAX_URL_LENGTH = 2048; // the server's limit

/** One line, as every address bar is; a long address scrolls inside it and shows whole on hover. */
function UrlInput({ value, onChange, invalid }) {
  return (
    <div className="relative">
      <Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input
        value={value}
        maxLength={MAX_URL_LENGTH}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://api.yourcompany.com/v1"
        aria-label="Base URL"
        title={value || undefined}
        spellCheck={false}
        className={`${inputClass(invalid)} pl-9 font-mono`}
      />
      {value.length > MAX_URL_LENGTH * 0.9 && (
        <p className="mt-1 text-right text-xs text-gray-500">
          {value.length.toLocaleString()} / {MAX_URL_LENGTH.toLocaleString()}
        </p>
      )}
    </div>
  );
}


function CredentialFields({ auth, creds, keepSaved, setAuthField, setCreds, keyNameError, tokenUrlError }) {
  const savedHint = keepSaved ? "Saved — leave blank to keep it" : undefined;
  if (auth.kind === "api_key") {
    return (
      <>
        <Field label="Your API key" hint={savedHint}>
          <SecretInput value={creds.api_key || ""} onChange={(v) => setCreds({ api_key: v })} placeholder="Paste the key" />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Send it as">
            <select
              value={auth.location || "header"}
              onChange={(e) => setAuthField({ location: e.target.value })}
              className={inputClass(false)}
            >
              <option value="header">A header (most APIs)</option>
              <option value="query">Part of the URL (?key=…)</option>
            </select>
          </Field>
          <Field label="Named" hint="Check the docs; often X-API-Key." error={keyNameError}>
            <input
              value={auth.name || ""}
              onChange={(e) => setAuthField({ name: e.target.value })}
              placeholder="X-API-Key"
              className={`${inputClass(false)} font-mono`}
            />
          </Field>
        </div>
      </>
    );
  }
  if (auth.kind === "bearer") {
    return (
      <Field label="Access token" hint={savedHint}>
        <SecretInput value={creds.token || ""} onChange={(v) => setCreds({ token: v })} placeholder="Paste the token (without “Bearer”)" />
      </Field>
    );
  }
  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Client ID" hint={savedHint}>
          <input
            value={creds.client_id || ""}
            onChange={(e) => setCreds({ client_id: e.target.value })}
            placeholder="00000000-0000-0000-0000-000000000000"
            autoComplete="off"
            className={`${inputClass(false)} font-mono`}
          />
        </Field>
        <Field label="Client secret" hint={savedHint}>
          <SecretInput value={creds.client_secret || ""} onChange={(v) => setCreds({ client_secret: v })} placeholder="Paste the secret" />
        </Field>
      </div>
      <Field label="Token URL" error={tokenUrlError} hint="Where Orbit asks for a token. For Microsoft Entra it ends in /oauth2/v2.0/token.">
        <input
          value={auth.token_url || ""}
          onChange={(e) => setAuthField({ token_url: e.target.value })}
          placeholder="https://login.microsoftonline.com/{tenant}/oauth2/v2.0/token"
          className={`${inputClass(false)} font-mono`}
        />
      </Field>
      <Field label="Scope" optional>
        <input
          value={auth.scopes || ""}
          onChange={(e) => setAuthField({ scopes: e.target.value })}
          placeholder="api://your-app/.default"
          className={`${inputClass(false)} font-mono`}
        />
      </Field>
    </>
  );
}

function Notice({ tone, title, children }) {
  const styles = tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-900" : "border-gray-200 bg-white text-gray-800";
  return (
    <div role="status" className={`flex gap-3 rounded-2xl border p-4 text-sm ${styles}`}>
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <p className="font-medium">{title}</p>
        <p className="mt-0.5">{children}</p>
      </div>
    </div>
  );
}

function AdvancedSettings({ auth, baseUrl, headers, setHeaders, timeoutMs, setTimeoutMs, retries, setRetries }) {
  const setHeader = (i, patch) =>
    setHeaders(headers.map((h, j) => (j === i ? { ...h, ...patch } : h)));
  return (
    <SettingRow title="Advanced" hint="Sent with every call. Leave as-is unless your API needs it.">
      <div className="space-y-5">
        <div>
          <p className="mb-2 text-sm font-medium text-gray-900">Custom headers</p>
          <div className="space-y-2">
            {headers.map((h, i) => (
              <div key={i} className="flex gap-2">
                <input value={h.key} onChange={(e) => setHeader(i, { key: e.target.value })} placeholder="X-Api-Version" className={`${inputClass(false)} font-mono`} />
                <input value={h.value} onChange={(e) => setHeader(i, { value: e.target.value })} placeholder="2026-09" className={`${inputClass(false)} font-mono`} />
                <button
                  type="button"
                  aria-label="Remove header"
                  onClick={() => setHeaders(headers.filter((_, j) => j !== i))}
                  className="rounded-lg px-2 text-gray-400 hover:bg-gray-100 hover:text-rose-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setHeaders([...headers, { key: "", value: "" }])}
            className="mt-2 flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            <Plus className="h-4 w-4" /> Add header
          </button>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Timeout (ms)" hint="1,000 – 120,000">
            <input type="number" min={1000} max={120000} value={timeoutMs} onChange={(e) => setTimeoutMs(e.target.value)} className={inputClass(false)} />
          </Field>
          <Field label="Retries on failure" hint="0 – 5. Use 0 for calls that create things.">
            <input type="number" min={0} max={5} value={retries} onChange={(e) => setRetries(e.target.value)} className={inputClass(false)} />
          </Field>
        </div>
        <div>
          <p className="mb-2 text-sm font-medium text-gray-900">What Orbit sends</p>
          <CodeBlock>{authPreview(auth, baseUrl, headers)}</CodeBlock>
        </div>
      </div>
    </SettingRow>
  );
}

function authPreview(auth, baseUrl, headers) {
  const lines = [`GET ${baseUrl || "https://…"}/…`];
  if (auth.kind === "api_key" && auth.location === "query") lines[0] += `?${auth.name || "key"}=••••••••`;
  else if (auth.kind === "api_key") lines.push(`${auth.name || "X-API-Key"}: ••••••••`);
  if (auth.kind === "bearer") lines.push("Authorization: Bearer ••••••••");
  if (auth.kind === "oauth_client_credentials")
    lines.push("Authorization: Bearer <token from token URL, renewed automatically>");
  headers.filter((h) => h.key).forEach((h) => lines.push(`${h.key}: ${h.value}`));
  return lines.join("\n");
}

const CHECK_EXPLAINED = "Check connection makes one harmless request to see that Orbit can reach the server.";

/**
 * The check only asks whether the server answers; any HTTP status proves that. Many APIs have
 * nothing at their base address and answer 404 there, which would read as a failure, so the
 * status is shown only in the developer view.
 */
function describeCheck(check, devMode) {
  if (!check?.http_status) return CHECK_EXPLAINED;
  if (!devMode) return "Each call is checked on its own when you try it.";
  return (
    `The base URL answered HTTP ${check.http_status}. Any answer, even 404, means the server is ` +
    "reachable: many APIs have nothing at their base address."
  );
}

function statusTitle({ check, connected, ready, unsaved }) {
  if (check) return check.message;
  if (unsaved) return ready ? "You have unsaved changes" : "Fill in the address and sign-in details";
  return connected ? "Sign-in details saved" : "Not connected yet";
}

function statusDetail({ check, devMode, unsaved }) {
  if (check && unsaved) return "Checked as shown above, not saved yet. Save to keep these details.";
  if (unsaved) return "Check the connection any time; Save stores the details.";
  return describeCheck(check, devMode);
}

function ConnectionStatus({ connected, check, devMode, ready, checkable, unsaved, state, onSave, onCheck }) {
  const good = check ? check.reachable && check.signed_in !== false : connected && !unsaved;
  const failed = check && !good;
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-4 border-t px-6 py-4 ${
        good ? "border-emerald-200 bg-emerald-50/60" : failed ? "border-rose-200 bg-rose-50/60" : "border-gray-200 bg-gray-50"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {good ? (
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
        ) : (
          <AlertTriangle className={`mt-0.5 h-5 w-5 shrink-0 ${failed ? "text-rose-600" : unsaved && ready ? "text-gray-400" : "text-amber-500"}`} />
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 [overflow-wrap:anywhere]">{statusTitle({ check, connected, ready, unsaved })}</p>
          <p className="text-xs text-gray-600">{statusDetail({ check, devMode, unsaved })}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          disabled={!checkable || state !== "idle"}
          title={checkable ? "Makes one harmless request with what the form shows; nothing is saved" : "Enter a valid address first"}
          onClick={onCheck}
          className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {state === "checking" && <Loader2 className="h-4 w-4 animate-spin" />}
          {state === "checking" ? "Checking…" : "Check connection"}
        </button>
        <button
          type="button"
          disabled={!unsaved || !ready || state !== "idle"}
          onClick={onSave}
          className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none"
        >
          {state === "saving" && <Loader2 className="h-4 w-4 animate-spin" />}
          {state === "saving" ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
